export const DelayedUpdateAccount =
  ({ AccountService, UIService, pStateToReduxAccount }) =>
  (account) => {
    const call = () => AccountService.setAccount(pStateToReduxAccount(account))
    UIService.Timer.callAfterTimeout(call)
  }

export const DeleteAccount =
  ({ AccountService, RestService, UIService }) =>
  async () => {
    const { id: accountId, name } = AccountService.getState().account
    const text = `Are you sure you want to delete ${name}?`
    const doDelete = await UIService.ConfirmationModal.show({ text })
    if (doDelete) {
      await RestService.delete(`/api/admin/accounts/${accountId}`)
      AccountService.clearEditAccount()
      AccountService.clearAccount()
      AccountService.removeAccountFromAccounts(accountId)
      UIService.EditAccount.close()
    }
  }

export const EditAccount =
  ({
    AccountService,
    RestService,
    UIService,
    pResponseEditAccount,
    pResponseEditClientAccounting,
  }) =>
  async () => {
    const accountId = AccountService.getState().account.id
    let account = await RestService.get(`/api/admin/accounts/${accountId}`)
    const accounting = await RestService.get(`/accounting/client/${accountId}`)
    account = {
      ...pResponseEditAccount(account),
      ...pResponseEditClientAccounting(accounting),
    }
    AccountService.setEditAccount(account)
    UIService.EditAccount.show()
  }

export const SaveAccount =
  ({
    AccountService,
    RestService,
    UIService,
    pRequestUpdateAccount,
    pRequestUpsertAccountingClient,
    pResponseAccount,
    pResponseEditClientAccounting,
    HandleError,
  }) =>
  async (data) => {
    UIService.Errors.clear()

    if (data.hubspotId && data.needHubspotSync) {
      const isLinked = await CheckCompanyLinked({ RestService })(data.hubspotId)
      if (isLinked) {
        UIService.FlashMessage.displayFailureMessage(
          'The selected hubspot company is already linked to another HUNGRY account',
        )

        return
      }
    }

    const isUpdate = Boolean(data.id)
    const accountData = pRequestUpdateAccount(data, isUpdate)
    let account
    try {
      // rails
      if (isUpdate) {
        account = await RestService.put(
          `/api/admin/accounts/${accountData.account.id}`,
          accountData,
          { timeout: 30000 },
        )
      } else {
        account = await RestService.post('/api/admin/accounts', accountData, {
          timeout: 30000,
        })

        await RestService.post('/client-reporting-programs', {
          name: `${account.name} - HUNGRY Orders`,
          clientId: account.id,
          dinerProfileIds: [],
          isDefault: true,
          disabled: false,
        })
      }

      // when accounts are created in rails it also creates an accounting client - need to wait to update it after is finished
      if (!isUpdate) {
        await new Promise((resolve) => setTimeout(resolve, 3000))
      }

      // accounting
      const accountingData = pRequestUpsertAccountingClient(data, account)
      const accounting = await RestService.put(
        `/accounting/client/${account.id}`,
        accountingData,
      )
      account = {
        ...pResponseAccount(account),
        ...pResponseEditClientAccounting(accounting),
      }

      // hubspot
      if (data.needHubspotSync) {
        // will sync companiesCache collection
        // neccessary for new clients created from hubspot
        const { id: hungryId, name, hubspotId } = account
        const params = { hubspotId: hubspotId, hungryId: hungryId, name }
        await RestService.post('/hubspot/companies', params)
      }

      AccountService.setAccount(account)
      UIService.EditAccount.close()
      AccountService.clearEditAccount()
    } catch (error) {
      HandleError({ error, namespace: 'accountModal' })
    }
  }

export const SavePreferenceProfile =
  ({
    UIService,
    RestService,
    pRequestUpdatePreferenceProfile,
    pResponsePreferenceProfile,
    HandleError,
  }) =>
  async (accountId, data) => {
    try {
      const profileData = pRequestUpdatePreferenceProfile(data)
      const profile = await RestService.put(
        `/api/admin/preference_profiles/${accountId}`,
        profileData,
      )
      UIService.FlashMessage.displaySuccessMessage(
        'Your changes have been saved!',
      )

      return pResponsePreferenceProfile(profile)
    } catch (error) {
      HandleError({ error, namespace: 'preferenceProfile' })
    }
  }

export const CloseEditAccount =
  ({ AccountService, UIService }) =>
  () => {
    UIService.EditAccount.close()
    AccountService.clearEditAccount()
  }

export const BulkUpdateHubspotAccounts =
  ({
    RestService,
    AccountService,
    HandleError,
    pResponseHubspotAccounts,
    pRequestBulkUpdateHubspotAccounts,
  }) =>
  async (data) => {
    try {
      const req = pRequestBulkUpdateHubspotAccounts(data)
      const response = await RestService.put(
        '/api/admin/accounts/hubspot/bulk_update',
        req,
        { timeout: 30000 },
      )
      const accounts = pResponseHubspotAccounts(response)
      AccountService.setAccountsInAccounts(accounts)

      return true
    } catch (error) {
      HandleError({ error })

      return false
    }
  }

export const CheckCompanyLinked =
  ({ RestService }) =>
  async (hubspotId) => {
    const response = await RestService.get(
      `/hubspot/companies/${hubspotId}/check-link`,
    )

    return response.isLinked
  }
